'use strict'

import { getMessage } from '../../../../common/utils/MessageUtils'
import { checkIfCorporationNumberInUse } from './services'
import { startOfMonth, endOfMonth, isAfter, formatDate as _formatDate, parse, isBefore } from 'date-fns'
import { produce } from 'immer'

/**
 * 重複チェックロジック
 *
 * @param {GroupCompanyInfo[]} groupCompanyList
 * @param {GroupCompany} groupCompany
 * @param {keyof GroupCompanyInfo} property
 * @param {number | null} editIndex
 * @returns {[boolean, string[]]}
 */
const checkIfDuplicateProperty = (groupCompanyList, groupCompany, property, editIndex) => {
  const errors = []
  const updatedCorporationList =
    editIndex !== null
      ? produce(groupCompanyList, (draft) => {
          draft[editIndex] = groupCompany
        })
      : [...groupCompanyList, groupCompany]

  const filteredCorporationList = updatedCorporationList.filter(
    (e) => e.operationType !== 'delete' && e[property] === groupCompany[property]
  )

  const sortedCorporationList = filteredCorporationList.sort((a, b) => {
    if (a.validStartDate === null && b.validStartDate === null) return 0
    if (a.validStartDate === null) return 1
    if (b.validStartDate === null) return -1
    return isAfter(a.validStartDate, b.validStartDate) ? 1 : -1
  })

  const isSerial = sortedCorporationList.every((e, index) => {
    if (index === 0) return true
    const prevValidEndDate = sortedCorporationList[index - 1].validEndDate
    if (e.validStartDate === null) return false
    if (prevValidEndDate === null) return false
    return isAfter(e.validStartDate, prevValidEndDate)
  })

  if (!isSerial) {
    const messageCode = property === 'compName' ? 'E011015' : 'E011014'
    errors.push(getMessage(messageCode, groupCompany[property || 'corporationNumber']))
  }

  return [!!errors.length, errors]
}

/**
 * 法人番号重複チェック
 *
 * @function checkIfDuplicateCompNumber
 * @param {GroupCompanyInfo[]} groupCompanyList
 * @param {GroupCompanyInfo} groupCompany
 * @param {number | null} editIndex
 * @returns {[boolean, string[]]}
 */
export const checkIfDuplicateCompNumber = (groupCompanyList, groupCompany, editIndex = null) => {
  return checkIfDuplicateProperty(groupCompanyList, groupCompany, 'corporationNumber', editIndex)
}

/**
 * 法人番号重複チェック
 *
 * @function checkIfDuplicateCompName
 * @param {GroupCompanyInfo[]} groupCompanyList
 * @param {GroupCompanyInfo} groupCompany
 * @param {number | null} editIndex
 * @returns {[boolean, string[]]}
 */
export const checkIfDuplicateCompName = (groupCompanyList, groupCompany, editIndex = null) => {
  return checkIfDuplicateProperty(groupCompanyList, groupCompany, 'compName', editIndex)
}

/**
 * 有効期限チェック
 *
 * @function checkIfValidPeriodCorrect
 * @param {GroupCompanyInfo} company
 * @param {GroupCompanyInfo[]} groupCompanyList
 * @returns {[boolean, string[]]}
 */
export const checkIfValidPeriodCorrect = (company, groupCompanyList, editIndex = null) => {
  const { validStartDate, validEndDate, corporationNumber } = company

  const errors = []

  if (validStartDate !== null && validEndDate !== null) {
    const validStartDateOfMonth = startOfMonth(validStartDate)
    const validEndDateOfMonth = endOfMonth(validEndDate)

    // 日付チェック
    if (isAfter(validStartDateOfMonth, validEndDateOfMonth)) {
      // 　[有効開始月] <= [有効終了月]
      errors.push(getMessage('E011013', '有効終了月', '有効開始月'))
    }
  }

  const createDuplicateCheck = (_validStartDate, _validEndDate) => ({
    start: _validStartDate !== null ? _formatDate(_validStartDate, 'yyyy年MM月') : '',
    end: _validEndDate !== null ? _formatDate(_validEndDate, 'yyyy年MM月') : ''
  })

  const hasDuplicate = groupCompanyList.some((e) => {
    const { validStartDate: _validStartDate, validEndDate: _validEndDate } = e

    const compare = createDuplicateCheck(_validStartDate, _validEndDate)
    const input = createDuplicateCheck(validStartDate, validEndDate)

    return e.corporationNumber === corporationNumber && compare.start === input.start && compare.end === input.end
  })

  if (hasDuplicate && editIndex === null) {
    errors.push(getMessage('E011014', corporationNumber))
  }

  return [!!errors.length, errors]
}

/** @function checkIfGroupCompanyInUse */
export const checkIfGroupCompanyInUse = async (groupCompanyList) => {
  const deletedList = groupCompanyList.filter((e) => e.operationType === 'delete')

  try {
    for (const { isInUse } of deletedList) {
      if (isInUse) throw Error('利用中です')
    }
    return false
  } catch (e) {
    console.log(e)

    return true
  }
}

/**
 * @function checkIfCompanyValid 会社が有効確認
 * @param {Date | null} validStartDate
 * @param {Date | null} validEndDate
 */
export const checkIfCompanyValid = (validStartDate, validEndDate) => {
  if (validEndDate == null && validStartDate === null) {
    return true
  }

  const start = startOfMonth(validStartDate)
  const end = endOfMonth(validEndDate)

  if (validEndDate === null) {
    return isAfter(new Date(), start)
  }

  return isAfter(new Date(), start) && isAfter(end, new Date())
}

/**
 * @function mergeItems
 * @param {GroupCompanyInfoResponse[]} groupCompanyInfoList,
 * @param {{ Corporation: { CorporationList: { Number: string; Name: string }[] }[] }} analysisTargetInfo
 * @returns {Object[]}
 */
export const mergeGroupCompanyData = (groupCompanyInfoList = [], analysisTargetInfo, companyList = []) => {
  const targetInfoCorporations = analysisTargetInfo.Corporation.flatMap((e) =>
    (e.CorporationList || []).map((x) => [x.Number, x])
  )
  const analysisTargetInfoMap = new Map(targetInfoCorporations)

  const companyMap = new Map(companyList.map((e) => [e.CorporationNumber, e?.ReportingCompanyCategory || '']))
  const registered = new Set()

  const collect = []
  groupCompanyInfoList.sort((a, b) => a.DisplayOrder - b.DisplayOrder)

  let maxDisplayOrder = 0

  for (const company of groupCompanyInfoList) {
    const { SubCorporationNumber, DisplayOrder } = company || {}

    const isChrofyEnabled = analysisTargetInfoMap.has(SubCorporationNumber)
    const compType = companyMap.get(SubCorporationNumber)

    collect.push(convertResponseToState(company, isChrofyEnabled, compType))

    if (isChrofyEnabled) registered.add(SubCorporationNumber)
    maxDisplayOrder = Math.max(maxDisplayOrder, DisplayOrder)
  }

  let counter = 1
  for (const [, e] of targetInfoCorporations.filter((e) => !registered.has(e[0]))) {
    const compType = companyMap.get(e.Number)
    collect.push({
      operationType: 'create',
      key: crypto.randomUUID(),
      compName: e.Name,
      compType: compType || '',
      validStartDate: null,
      validEndDate: null,
      corporationNumber: e.Number,
      displayOrder: maxDisplayOrder + counter,
      memo: '',
      isDeleted: false,
      isRegistered: false,
      isChrofyEnabled: true
    })
    counter++
  }

  return collect
}

export const parseDate = (date) => {
  if (!date) return null
  const result = parse(date, 'yyyy/MM/dd', new Date())

  if (isNaN(result.getTime())) return null

  return result
}

/**
 * @function convertResponseToState
 * @param {GroupCompanyInfoResponse} company
 * @param {boolean} isChrofyEnabled
 * @param {string} compType
 */
export const convertResponseToState = (company = {}) => {
  const {
    Key,
    CompName,
    CompType,
    ValidStartDate = null,
    ValidEndDate = null,
    SubCorporationNumber,
    DisplayOrder,
    Memo,
    IsChrofyEnabled = false,
    IsInUse = false,
    CreateDateTime
  } = company

  const isRegistered = CreateDateTime !== ''

  return {
    operationType: isRegistered ? '' : 'create',
    key: Key,
    compName: CompName,
    compType: CompType || '',
    corporationNumber: SubCorporationNumber,
    validStartDate: parseDate(ValidStartDate),
    validEndDate: parseDate(ValidEndDate),
    displayOrder: DisplayOrder,
    memo: Memo,
    isDeleted: false,
    isRegistered,
    isChrofyEnabled: IsChrofyEnabled,
    isInUse: IsInUse
  }
}
